// Learning Moments observability — lists detection runs, one row per call:
// a per-question run (one submission) or a whole-sitting run (one call over a
// chunk of a sitting's questions), with the moments it detected inline.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import type { LearningMomentRun } from '../../types';
import { DataTable, type ColumnDef } from '@shared-ui/components';
import { LatexText } from '@shared-ui/latex';

const pageSize = 20;

const formatLatency = (ms?: number) => (ms ? `${(ms / 1000).toFixed(1)}s` : '-');
const formatCost = (c?: number) => (typeof c === 'number' ? `$${c.toFixed(4)}` : '-');

export function LearningMomentsList() {
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['learningMomentRuns', page],
    queryFn: () => adminApi.getLearningMomentRuns({ page, pageSize }),
  });

  const rows = data?.runs || [];
  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const columns: ColumnDef<LearningMomentRun>[] = useMemo(() => [
    {
      key: 'date',
      header: 'Date',
      className: 'dt-col-date',
      render: (r) => (
        <div className="dc-stack">
          <span className="dc-date">{format(new Date(r.generatedAt), 'MMM d, HH:mm')}</span>
        </div>
      ),
    },
    {
      key: 'user',
      header: 'User',
      render: (r) => (
        <div className="dc-stack">
          <span className="dc-name">{r.user?.name || 'Unknown'}</span>
          <span className="dc-email">{r.user?.email || '-'}</span>
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      className: 'dt-col-subject',
      render: (r) => {
        const subj = r.question?.subject || '';
        return (
          <div className="dc-stack">
            <span className={`dc-subject ${subj ? `dc-subject--${subj}` : ''}`}>{subj || '-'}</span>
            <span className="dc-topic">{r.question?.topic || '-'}</span>
          </div>
        );
      },
    },
    {
      key: 'question',
      header: 'Question',
      render: (r) => {
        if (r.sitting) {
          const n = r.sitting.numbers;
          return (
            <div className="dc-stack">
              <span className="dc-name">Whole sitting · {n.length} of {r.sitting.of} question{r.sitting.of === 1 ? '' : 's'}</span>
              <span className="dc-topic">Q {n.join(', ')}</span>
            </div>
          );
        }
        return r.question?.text
          ? <LatexText text={r.question.text} truncate={100} />
          : <span className="dc-no-value">-</span>;
      },
    },
    {
      key: 'moments',
      header: 'Moments Detected',
      render: (r) => {
        if (r.moments.length === 0) return <span className="dc-no-value">none</span>;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {r.moments.map((m, i) => (
              <span
                key={`${m.learningMomentId}-${i}`}
                title={m.context}
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  background: 'var(--bg-tertiary)',
                  color: m.wasPositive ? 'var(--color-success, #16a34a)' : 'var(--color-error, #dc2626)',
                }}
              >
                {m.wasPositive ? '✓' : '✗'} {m.q !== undefined ? `Q${m.q} · ` : ''}{m.name}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'latency',
      header: 'Latency',
      render: (r) => formatLatency(r.latencyMs),
    },
    {
      key: 'cost',
      header: 'Cost',
      render: (r) => formatCost(r.cost),
    },
  ], []);

  if (error) return <div className="error">Failed to load learning-moment runs</div>;

  return (
    <DataTable
      columns={columns}
      data={rows}
      rowKey={(r) => r.id}
      pagination={{
        page,
        totalPages,
        total: data?.total,
        onPageChange: setPage,
      }}
      loading={isLoading}
      emptyMessage="No learning-moment runs"
    />
  );
}
